import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { ROLES, type AdminUser, type InvitationResult, type ManagerColor, type Role } from "@salesboard/contracts";
import { Button } from "@/components/ui/button";
import { ColourPicker } from "@/components/ui/ColourPicker";
import { FormDialog } from "@/components/ui/FormDialog";
import { Field, Input, Select } from "@/components/ui/input";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { ApiClientError } from "@/lib/api";
import { useChangeUserRole, useCreateUser, useSetUserColor } from "./hooks";

/**
 * Create a user, and optionally invite them.
 *
 * Sending is a checkbox, not an automatic consequence — the pattern bd-admin arrived at,
 * which avoids accidental invites and keeps create → check → send as separate decisions.
 * Either way the link is shown afterwards, because the book requires a path for handing it
 * over when an address is wrong or the person is in the room.
 */
export function UserFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(ROLES.salesManager);
  const [color, setColor] = useState<ManagerColor | null>(null);
  const [sendInvitation, setSendInvitation] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [invitation, setInvitation] = useState<InvitationResult>();

  const create = useCreateUser();

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(undefined);

    try {
      const result = await create.mutateAsync({ name, email, role, color, sendInvitation });
      // The dialog stays open on success to show the link — closing it would throw away
      // the one copy of a token that is never recoverable afterwards.
      setInvitation(result.invitation);
    } catch (error) {
      if (error instanceof ApiClientError && error.isValidation) {
        const flat: Record<string, string> = {};
        for (const [field, messages] of Object.entries(error.fieldErrors)) {
          if (messages[0]) flat[field] = messages[0];
        }
        setFieldErrors(flat);
        return;
      }
      setFormError(error instanceof ApiClientError ? error.message : t("toast.genericFailure"));
    }
  };

  if (invitation) {
    return (
      <FormDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setInvitation(undefined);
            setName("");
            setEmail("");
            setColor(null);
          }
          onOpenChange(next);
        }}
        title={t("userForm.createdTitle", name)}
        description={invitation.emailSent ? t("userForm.emailSent") : t("userForm.emailNotSent")}
        submitLabel={t("common.done")}
        onSubmit={(event) => {
          event.preventDefault();
          setInvitation(undefined);
          onOpenChange(false);
        }}
      >
        <InvitationLink link={invitation.link} expiresAt={invitation.expiresAt} />
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("userForm.title")}
      description={t("userForm.description")}
      submitLabel={t("userForm.submit")}
      pending={create.isPending}
      error={formError}
      onSubmit={submit}
    >
      <Field label={t("userForm.name")} htmlFor="user-name" error={fieldErrors.name}>
        <Input id="user-name" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>

      <Field label={t("userForm.email")} htmlFor="user-email" error={fieldErrors.email}>
        <Input
          id="user-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>

      <Field label={t("userForm.role")} htmlFor="user-role" error={fieldErrors.role}>
        <Select id="user-role" value={role} onChange={(event) => setRole(event.target.value as Role)}>
          <option value={ROLES.salesManager}>{t("role.sales_manager")}</option>
          <option value={ROLES.headOfSales}>{t("role.head_of_sales")}</option>
        </Select>
      </Field>

      {/*
        Colour, at creation (2026-08-26). It used to be assignable only from the row afterwards,
        so every new manager spent their first days uncoloured on a shared board. Optional here
        as it is everywhere: "decide later" is a real answer, and the board reads such a manager
        as neutral rather than as broken.
      */}
      <Field label={t("column.colour")} htmlFor="user-colour" error={fieldErrors.color}>
        <ColourPicker
          id="user-colour"
          label={t("colour.forNewUser")}
          triggerLabel={t(color ? `colour.${color}` : "colour.none")}
          value={color}
          onChoose={setColor}
          note={color === null ? t("colour.neutralNote") : undefined}
        />
      </Field>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={sendInvitation}
          onChange={(event) => setSendInvitation(event.target.checked)}
          className="accent-primary mt-0.5 h-4 w-4"
        />
        <span>
          {t("userForm.sendInvitation")}
          <span className="text-muted-foreground block text-xs">{t("userForm.sendInvitationHint")}</span>
        </span>
      </label>
    </FormDialog>
  );
}

/**
 * Edit a user: their role and their colour, in one dialog.
 *
 * Both were editable *in the row* — a select and a swatch popover per line, which made the
 * table a form and pushed the columns carrying facts off the side (2026-08-26). The row is
 * read-only now and this is where a change is made, which also makes the two writes one
 * decision the reader confirms once.
 *
 * **Two mutations, chained.** The API has a route per attribute, each carrying a version, so
 * saving both in one submit means using the version the *first* response returns — sending the
 * row's original twice would fail the second write as a stale edit. Nothing is sent for an
 * attribute that did not change.
 */
export function UserEditDialog({
  user,
  isSelf,
  onOpenChange,
}: {
  user: AdminUser;
  /** The reader's own row. Passed in rather than derived: the session lives on the page. */
  isSelf: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const t = useT();
  const changeRole = useChangeUserRole();
  const setColor = useSetUserColor();
  const [role, setRole] = useState<Role>(user.role);
  const [color, setColorDraft] = useState<ManagerColor | null>(user.color);
  const [formError, setFormError] = useState<string>();

  // You cannot change your own role — doing so could lock the last administrator out of the
  // screen that would restore them. The dialog says so rather than silently ignoring it.
  const ownRole = isSelf;
  const dirty = (role !== user.role && !ownRole) || color !== user.color;

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(undefined);

    try {
      let version = user.version;
      if (role !== user.role && !ownRole) {
        const updated = await changeRole.mutateAsync({ id: user.id, role, version });
        version = updated.version;
      }
      if (color !== user.color) {
        await setColor.mutateAsync({ id: user.id, color, version });
      }
      onOpenChange(false);
    } catch (error) {
      setFormError(error instanceof ApiClientError ? error.message : t("toast.genericFailure"));
    }
  };

  return (
    <FormDialog
      open
      onOpenChange={onOpenChange}
      title={t("users.editTitle", user.name)}
      description={user.email}
      submitLabel={t("common.saveChanges")}
      pending={changeRole.isPending || setColor.isPending}
      submitDisabled={!dirty}
      error={formError}
      onSubmit={submit}
    >
      <Field
        label={t("column.role")}
        htmlFor="edit-user-role"
        hint={ownRole ? t("users.ownRoleLocked") : undefined}
      >
        <Select
          id="edit-user-role"
          value={role}
          disabled={ownRole}
          onChange={(event) => setRole(event.target.value as Role)}
        >
          <option value={ROLES.salesManager}>{t("role.sales_manager")}</option>
          <option value={ROLES.headOfSales}>{t("role.head_of_sales")}</option>
        </Select>
      </Field>

      <Field label={t("column.colour")} htmlFor="edit-user-colour">
        <ColourPicker
          id="edit-user-colour"
          label={t("colour.forUser", user.name)}
          triggerLabel={t(color ? `colour.${color}` : "colour.none")}
          value={color}
          // A deactivated colleague keeps their stored colour so historical cards stay
          // tinted, but they are not someone to assign a colour to.
          disabled={!user.active}
          onChoose={setColorDraft}
          note={color === null ? t("colour.neutralNote") : undefined}
        />
      </Field>
    </FormDialog>
  );
}

/** The link is shown once. There is no way to recover it afterwards — only to re-issue. */
export function InvitationLink({ link, expiresAt }: { link: string; expiresAt: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  return (
    <div className="space-y-2">
      <div className="bg-muted flex items-center gap-2 rounded-md p-2">
        <code className="min-w-0 flex-1 truncate text-xs">{link}</code>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
        >
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
          {copied ? t("common.copied") : t("common.copy")}
        </Button>
      </div>
      {/* Through the shared formatter, not `toLocaleString()`: a bare call formats in the
          *browser's* locale, which is not the one the reader chose. */}
      <p className="text-muted-foreground text-xs">{t("invitation.validUntil", formatDateTime(expiresAt))}</p>
    </div>
  );
}
